/**
 * Holds the state of view
 * @module
 */
var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var logger = require('../utils/logger');

var EXPERIMENTAL_EDGE_SPA = __EXPERIMENTAL_EDGE_SPA__;
var EXPERIMENTAL_WEB_SDK = __EXPERIMENTAL_WEB_SDK__;

var initialState = {
  globalTags: {},
  viewStates: {},
  shouldBatch: false,
};

module.exports = {
  initialize: function() {
    this.__state = _.cloneDeep(initialState);

    this.on(actionTypes.REGISTER_VIEWS, this.__registerViews);
    this.on(actionTypes.SET_VIEW_ACTIVE_STATE, this.__setIsActive);
    this.on(actionTypes.UPDATE_PARSED_VIEW_METADATA, this.__updateParsedMetadata);
    this.on(actionTypes.UPDATE_USER_SUPPLIED_METADATA, this.__updateUserSuppliedMetadata);
    this.on(actionTypes.TRACK_VIEW_ACTIVATED_EVENT, this.__setViewEventId);
    this.on(actionTypes.SET_GLOBAL_TAGS, this.__setGlobalTags);
    this.on(actionTypes.RESET_VIEW_STATES, this.__resetViewStates);
    this.on(actionTypes.SET_VIEW_BATCHING, this.__setBatching);
  },

  /**
   * Get all view states
   * @returns {ViewState[]}
   */
  getAll: function(): ViewState[] {
    var allViewStates = {} as any;
    for (var key in this.__state.viewStates) {
      allViewStates[key] = this.getViewState(key);
    }
    return allViewStates;
  },

  /**
   * Get all active view ids
   * @returns {string[]}
   */
  getActiveViewIds: function(): string[] {
    return _.reduce(this.__state.viewStates, _.bind(function(memo, value, pageId) {
      if (this.isViewActive(pageId)) {
        memo.push(pageId);
      }
      return memo;
    }, this), []);
  },

  /**
   * @return {boolean} if view activations should be batched
   */
  shouldBatch: function(): boolean {
    return this.__state.shouldBatch;
  },

  /**
   * Gets an individual view state
   * @param {number} pageId
   * @returns {ViewState}
   */
  getViewState: function(pageId:number): ViewState {
    var viewState = _.cloneDeep(this.__state.viewStates[pageId]);
    var globalTags = this.__state.globalTags;

    // Collect all page metadata giving priority to user supplied metadata
    viewState.metadata = _.extend(
      {}, viewState.parsedMetadata, globalTags, viewState.userSuppliedMetadata);

    if (__INVARIANT__) {
      require('../utils/invariant').isType('ViewState', viewState);
    }

    return viewState;
  },

  /**
   * Gets collapsed / merged view tags for all active views
   * @return {Object}
   */
  getActiveViewTags: function(): any {
    var activeViewStates = this.getActiveViewStates();
    var tags = _.map(activeViewStates, function(viewState) {
      return viewState.metadata;
    });
    var extendArgs = [{}].concat(tags);
    // collapse active view tags into single object
    return _.extend.apply(_, extendArgs);
  },

  /**
   * @param {string} pageId
   * @return {string|null} eventId of the view_activated event, or null if not tracked
   */
  getActivationEventId: function(pageId:string): string|null {
    if (!this.__state.viewStates[pageId]) {
      return null;
    }

    return this.__state.viewStates[pageId].activationEventId;
  },

  /**
   * Returns only the active views
   * @return {ViewState[]}
   */
  getActiveViewStates: function(): ViewState[] {
    // no need to clone deep here (`getViewState` already clones)
    return _.reduce(this.__state.viewStates, _.bind(function(memo, value, pageId) {
      if (this.isViewActive(pageId)) {
        memo.push(this.getViewState(pageId));
      }
      return memo;
    }, this), []);
  },

  /**
   * @param {string} pageId
   * @return {boolean}
   */
  isViewActive: function(pageId:string): boolean {
    var view = this.__state.viewStates[pageId];
    if (__INVARIANT__ && !view) {
      throw new Error('No view for pageId = ' + pageId);
    } else if (!view) {
      logger.warn('No Page registered with id', pageId);
    }

    return !!view.isActive;
  },

  /**
   *
   * @returns {Object.<*>}
   */
  getGlobalTags: function(): Record<string, any> {
    return _.cloneDeep(this.__state.globalTags);
  },

  /**
   * Reset the viewStates property of the store
   */
  __resetViewStates: function() {
    this.__state.viewStates = {} as any;
    this.emitChange();
  },

  /**
   * Add new view to the store
   * @private
   * @param {Object} payload
   * @param {Array.<Object>} payload.views
            Objects with properties `id` (of a view) and optionally `isActive`.
            Presence of `isActive` denotes that view activation conditions were fully
            evaluated; absence denotes that they weren't.
   */
  __registerViews: function(payload:any) {
    const forceClean = payload.forceClean || false;

    _.each(payload.views, _.bind(function(view) {
      var pageId = view.id;
      if ((EXPERIMENTAL_EDGE_SPA || EXPERIMENTAL_WEB_SDK) && this.__state.viewStates[pageId] && !forceClean) {
        // View already registered by initialViewStates API, skip reregistering view
        return;
      }
      this.__state.viewStates[pageId] = {
        id: pageId,
        isActive: _.isBoolean(view.isActive) && !forceClean ? view.isActive : null,
        activatedTimestamp: null,
        activationEventId: null,
        // key value pairs of optimizely parsed "page metadata" through tags
        parsedMetadata: {},
        // key value pairs of user set "page metadata" through the js api
        userSuppliedMetadata: {},
      };
    }, this));
    this.emitChange();
  },

  /**
   * Sets the view isActive state
   * @private
   * @param {Object} payload
   * @param {View} payload.view
   * @param {number} payload.timestamp
   * @param {boolean} payload.isActive
   */
  __setIsActive: function(payload:any) {
    var pageId = payload.view.id;
    if (!this.__state.viewStates[pageId]) {
      throw new Error('No view exists with id ' + pageId);
    }
    this.__state.viewStates[pageId].isActive = payload.isActive;
    if (payload.isActive) {
      this.__state.viewStates[pageId].activatedTimestamp = payload.timestamp;
    } else {
      this.__state.viewStates[pageId].parsedMetadata = {} as any;
      this.__state.viewStates[pageId].userSuppliedMetadata = {} as any;
    }
    this.emitChange();
  },

  /**
   * Persists updated parsed view metadata
   * @param {Object} payload
   * @param {string} payload.pageId
   * @param {Object} payload.metadata Map of tag name to tag value
   *  (provided values will overwrite existing values for the same tag)
   */
  __updateParsedMetadata: function(payload:any) {
    var pageId = payload.pageId;
    if (!this.__state.viewStates[pageId]) {
      throw new Error('No view exists with id ' + pageId);
    }
    _.assign(this.__state.viewStates[pageId].parsedMetadata, payload.metadata);

    this.emitChange();
  },

  /**
   * Persists user supplied metadata fields
   * This metadata takes priority over automatically parsed metadata fields
   * @param {Object} payload
   * @param {string} payload.pageId
   * @param {Object} payload.metadata Map of tag name to tag value
   *  (provided values will overwrite existing values for the same tag)
   */
  __updateUserSuppliedMetadata: function(payload:any) {
    var pageId = payload.pageId;
    if (!this.__state.viewStates[pageId]) {
      throw new Error('No view exists with id ' + pageId);
    }
    _.assign(this.__state.viewStates[pageId].userSuppliedMetadata, payload.metadata);

    this.emitChange();
  },

  /**
   * Persists updated view metadata
   * @param {Object} payload
   * @param {string} payload.pageId
   * @param {Object} payload.eventData
   */
  __setViewEventId: function(payload:any) {
    var pageId = payload.pageId;
    if (!this.__state.viewStates[pageId]) {
      // not a critical error, so dont throw
      return;
    }

    this.__state.viewStates[pageId].activationEventId = payload.eventData.eventId;
    this.emitChange();
  },

  /**
   *
   * @param {Object.<*>} payload
   * @private
   */
  __setGlobalTags: function(payload:Record<string, any>) {
    _.extend(this.__state.globalTags, payload);
    this.emitChange();
  },

  __setBatching: function(value) {
    this.__state.shouldBatch = value;
    this.emitChange();
  },
};

if (__TEST__) {
  module.exports.setEdgeSpa = function(edgeSpa) {
    EXPERIMENTAL_EDGE_SPA = edgeSpa;
  };
}
